/**
 * Smooth Infinite Scroll for Siedrés Collection Pages
 * 
 * Inspired by Jacquemus implementation - preloads next page content
 * and smoothly reveals products as user scrolls down.
 */

class SmoothInfiniteScroll {
  constructor() {
    console.log('SmoothInfiniteScroll: Constructor started...');
    
    // Core elements
    this.productGrid = document.querySelector('.grid-outer');
    this.productContainer = document.querySelector('.grid#CollectionLoop');
    this.loadingIndicator = document.querySelector('.grid__loader');
    
    // Get collection information from section data attributes
    this.collectionSection = document.querySelector('.collection-products');
    
    console.log('SmoothInfiniteScroll: Core elements found?', {
      productGrid: !!this.productGrid,
      productContainer: !!this.productContainer,
      collectionSection: !!this.collectionSection,
      loadingIndicator: !!this.loadingIndicator
    });
    
    // State tracking
    this.isLoading = false;
    this.nextPageUrl = null;
    this.canLoadMore = false;
    this.allProductsLoaded = false;
    this.currentPage = 1; // Track the current page number
    
    // Make this instance globally available for other scripts
    window.infiniteScrollInstance = this;
    this.preloadedItems = [];
    this.preloadThreshold = 5; // Start preloading when 5 items remain before viewport
    this.itemsPerPage = 0; // Will be determined dynamically
    this.currentPage = 1;
    this.totalPages = 1;
    
    // Grid tracking for media block positioning
    this.gridColumns = 4;
    this.currentRowPosition = 1;
    
    // Important: Make sure we have only ONE observer
    this.observer = null;
    
    // Define custom events for other scripts to listen to
    this.customEvents = {
      pageLoaded: new CustomEvent('smooth-infinite-scroll:page-loaded', { bubbles: true }),
      itemsRevealed: new CustomEvent('smooth-infinite-scroll:items-revealed', { bubbles: true })
    };
    
    // Get current page and total pages from collection section data attributes
    if (this.collectionSection) {
      this.currentPage = parseInt(this.collectionSection.dataset.currentPage) || 1;
      this.totalPages = parseInt(this.collectionSection.dataset.totalPages) || 1;
      this.collectionUrl = this.collectionSection.dataset.collection || '/collections/all';
      
      console.log('SmoothInfiniteScroll: Collection info -', {
        currentPage: this.currentPage,
        totalPages: this.totalPages,
        collectionUrl: this.collectionUrl
      });
    }
    
    // Only initialize if we're on a collection page with the required elements
    if (this.productGrid && this.productContainer && this.collectionSection) {
      console.log('SmoothInfiniteScroll: Required elements found, initializing...');
      this.init();
    } else {
      console.error('SmoothInfiniteScroll: Missing required elements, cannot initialize');
      console.log('DOM Structure:', {
        body: document.body.innerHTML.substring(0, 200) + '...',
        productGridParent: document.querySelector('.grid-outer')?.parentElement,
        containerParent: document.querySelector('.grid#CollectionLoop')?.parentElement
      });
    }
  }

  init() {
    console.log('SmoothInfiniteScroll: Initializing...');
    
    // Check if we have more pages to load
    if (this.currentPage < this.totalPages) {
      console.log('SmoothInfiniteScroll: More pages available:', this.currentPage, 'of', this.totalPages);
      this.canLoadMore = true;
      
      // Construct the next page URL preserving current filters/sort
      const currentURL = new URL(window.location.href);
      currentURL.searchParams.set('page', this.currentPage + 1);
      this.nextPageUrl = currentURL.toString();
      
      console.log('SmoothInfiniteScroll: Constructed next page URL:', this.nextPageUrl);
    } else {
      console.log('SmoothInfiniteScroll: Already on the last page, no more pages to load');
      this.canLoadMore = false;
      this.allProductsLoaded = true;
    }
    
    // Add a class to the body for styling
    document.body.classList.add('has-infinite-scroll');
    
    // Configure loading indicator
    if (this.loadingIndicator) {
      this.loadingIndicator.style.opacity = '0';
      this.loadingIndicator.style.display = 'block';
    } else {
      console.warn('SmoothInfiniteScroll: Loading indicator not found');
    }
    
    // Count initial items to determine items per page - try different selectors
    let initialItems = this.productContainer.querySelectorAll('.grid-item.product-item');
    
    // If no items found, try alternative selectors
    if (initialItems.length === 0) {
      initialItems = this.productContainer.querySelectorAll('[data-grid-item]');
    }
    
    // If still no items, try any direct children of the container
    if (initialItems.length === 0) {
      initialItems = this.productContainer.querySelectorAll(':scope > *');
    }
    
    this.itemsPerPage = initialItems.length;
    console.log('SmoothInfiniteScroll: Found', this.itemsPerPage, 'initial items');
    
    // If no items found yet, wait a bit and try again - the items might be loaded dynamically
    if (this.itemsPerPage === 0) {
      console.log('SmoothInfiniteScroll: No items found initially, will try again in 1 second');
      setTimeout(() => {
        const retryItems = this.productContainer.querySelectorAll('.grid-item.product-item, [data-grid-item], :scope > *');
        this.itemsPerPage = retryItems.length;
        console.log('SmoothInfiniteScroll: After retry, found', this.itemsPerPage, 'items');
        
        // Setup Intersection Observer for last few items
        this.setupIntersectionObserver();
      }, 1000);
    } else {
      // Setup Intersection Observer for last few items
      this.setupIntersectionObserver();
    }
    
    // Start preloading the next page immediately if we can load more
    if (this.canLoadMore) {
      this.preloadNextPage();
    }
    
    console.log('SmoothInfiniteScroll: Initialization complete');
  }

  setupIntersectionObserver() {
    try {
      console.log('SmoothInfiniteScroll: Setting up Intersection Observer');
      
      // Clean up existing observer
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      
      // Debounce variable
      this._observerTimeout = null;
      
      // Create the observer with improved settings
      this.observer = new IntersectionObserver((entries) => {
        // Debounce logic to prevent multiple rapid calls
        if (this._observerTimeout) clearTimeout(this._observerTimeout);
        
        this._observerTimeout = setTimeout(() => {
          const hasIntersectingEntry = entries.some(entry => entry.isIntersecting);
          
          if (hasIntersectingEntry) {
            console.log('SmoothInfiniteScroll: Trigger conditions met, revealing items and preloading next page');
            
            // When one of our observed items enters viewport, reveal preloaded items
            // and preload the next page
            this.revealPreloadedItems();
            
            // Preload next page if we can load more
            if (this.canLoadMore && !this.isLoading) {
              this.preloadNextPage();
            }
          }
        }, 50); // Small timeout for better performance
      }, {
        rootMargin: '500px 0px', // Increased margin for earlier preloading
        threshold: 0.05 // Reduced threshold for earlier triggering
      });
      
      // Observe the trigger items
      this.updateObservedItems();
    } catch (error) {
      console.error('SmoothInfiniteScroll: Error setting up Intersection Observer:', error);
    }
  }
  
  updateObservedItems() {
    console.log('SmoothInfiniteScroll: Updating observed items...');
    
    // Check for observer initialization
    if (!this.observer) {
      console.warn('SmoothInfiniteScroll: Observer not initialized yet. Skipping updateObservedItems.');
      // Try to initialize the observer
      setTimeout(() => {
        this.setupIntersectionObserver();
      }, 500);
      return;
    }
    
    // Disconnect any existing observations
    try {
      this.observer.disconnect();
      console.log('SmoothInfiniteScroll: Disconnected previous observations');
    } catch (error) {
      console.error('SmoothInfiniteScroll: Error disconnecting observer:', error);
    }
    
    // Helper function to find grid items with different selectors
    const getGridItems = (container) => {
      let items = container.querySelectorAll('.grid-item.product-item:not(.product-item--featured-image)');
      if (items.length === 0) items = container.querySelectorAll('[data-grid-item]');
      if (items.length === 0) items = container.querySelectorAll(':scope > *');
      return items;
    };
    
    // Get all current product items (excluding media blocks)
    const allItems = getGridItems(this.productContainer);
    
    if (allItems.length === 0) {
      console.warn('SmoothInfiniteScroll: No items found to observe, will try again in 1 second');
      setTimeout(() => this.updateObservedItems(), 1000);
      return;
    }
    
    // Determine which items to observe
    const startIndex = Math.max(0, allItems.length - this.preloadThreshold);
    console.log('SmoothInfiniteScroll: Will observe items from index', startIndex, 'to', allItems.length - 1);
    
    // Observer logic...
    let observedCount = 0;
    for (let i = startIndex; i < allItems.length; i++) {
      // Ensure it's an Element before observing
      if (allItems[i] instanceof Element) {
        this.observer.observe(allItems[i]);
        observedCount++;
      } else {
        console.warn('SmoothInfiniteScroll: Item at index', i, 'is not an Element, cannot observe:', allItems[i]);
      }
    }
    
    console.log(`SmoothInfiniteScroll: Now observing ${observedCount} items`);
  }

  async preloadNextPage() {
    console.log('SmoothInfiniteScroll: preloadNextPage called. nextPageUrl:', this.nextPageUrl, 'isLoading:', this.isLoading, 'allProductsLoaded:', this.allProductsLoaded);
    
    if (!this.nextPageUrl || this.isLoading || this.allProductsLoaded) {
      console.log('SmoothInfiniteScroll: Preload conditions not met, returning');
      return;
    }
    
    this.isLoading = true;
    console.log('SmoothInfiniteScroll: Starting preload process...');
    
    // Show loading indicator subtly
    if (this.loadingIndicator) {
      this.loadingIndicator.style.opacity = '0.5';
      console.log('SmoothInfiniteScroll: Showing loading indicator');
    }
    
    try {
      console.log('SmoothInfiniteScroll: Fetching next page:', this.nextPageUrl);
      
      // Fetch the next page
      const response = await fetch(this.nextPageUrl);
      const html = await response.text();
      console.log('SmoothInfiniteScroll: Fetch successful, got HTML of length:', html.length);
      
      // Create a temporary element to parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find all product items in the fetched page - try different selectors based on the theme structure
      let newProductItems = doc.querySelectorAll('.grid#CollectionLoop .grid-item.product-item');
      
      // If no items found, try alternative selectors
      if (newProductItems.length === 0) {
        newProductItems = doc.querySelectorAll('.grid#CollectionLoop [data-grid-item]');
        console.log('SmoothInfiniteScroll: Tried alternative selector [data-grid-item], found', newProductItems.length, 'items');
      }
      
      // If still no items, try direct children of the container
      if (newProductItems.length === 0) {
        const collectionLoop = doc.querySelector('.grid#CollectionLoop');
        if (collectionLoop) {
          newProductItems = collectionLoop.querySelectorAll(':scope > *');
          console.log('SmoothInfiniteScroll: Tried direct children selector, found', newProductItems.length, 'items');
        }
      }
      
      console.log('SmoothInfiniteScroll: Found', newProductItems.length, 'new product items in fetched page');
      
      // If we found new items, add them to our preloaded items array
      if (newProductItems.length > 0) {
        console.log('SmoothInfiniteScroll: Processing new items...');
        
        // Include ALL items (products AND media blocks)
        const actualProductNodes = Array.from(newProductItems);

        console.log('SmoothInfiniteScroll: Total items to add (including media blocks):', actualProductNodes.length);

        // Clear previous preload cache before adding new items
        this.preloadedItems = [];

        // Store the items for later reveal
        actualProductNodes.forEach((item, index) => {
          // Clone the item to avoid reference issues
          const clonedItem = item.cloneNode(true);
          clonedItem.classList.add('infinite-scroll-item');
          this.preloadedItems.push(clonedItem);
          console.log('SmoothInfiniteScroll: Cloned product item', index + 1);
        });
        
        console.log('SmoothInfiniteScroll: Added', actualProductNodes.length, 'items to preloadedItems, total:', this.preloadedItems.length);
        
        // Update current page
        this.currentPage++;
        console.log('SmoothInfiniteScroll: Updated current page to:', this.currentPage);
        
        // Dispatch page changed event for other scripts (like CollectionMediaManager)
        document.dispatchEvent(new CustomEvent('infinite-scroll:page-changed', {
          detail: { page: this.currentPage, itemsPerPage: this.itemsPerPage }
        }));
        
        // Check if we've reached the last page
        if (this.currentPage >= this.totalPages) {
          console.log('SmoothInfiniteScroll: Reached last page');
          this.nextPageUrl = null;
          this.allProductsLoaded = true;
        } else {
          // Construct the next page URL for the next load preserving current filters/sort
          const currentURL = new URL(window.location.href);
          currentURL.searchParams.set('page', this.currentPage + 1);
          this.nextPageUrl = currentURL.toString();
          console.log('SmoothInfiniteScroll: Next URL for future loading:', this.nextPageUrl);
        
        // Dispatch custom event to notify other scripts
        document.dispatchEvent(this.customEvents.pageLoaded);
        }
        
        // Check if we should reveal items based on scroll position
        const scrollY = window.scrollY || window.pageYOffset;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const scrollPercentage = (scrollY + windowHeight) / documentHeight;
        
        console.log('SmoothInfiniteScroll: Scroll percentage:', scrollPercentage.toFixed(2));
        
        if (scrollPercentage > 0.8) {
          console.log('SmoothInfiniteScroll: Scroll percentage > 0.8, revealing items immediately');
          this.revealPreloadedItems();
        }
      } else {
        console.log('SmoothInfiniteScroll: No more products to load');
        this.nextPageUrl = null;
        this.allProductsLoaded = true;
      }
    } catch (error) {
      console.error('SmoothInfiniteScroll: Error preloading next page:', error);
      this.nextPageUrl = null;
      this.allProductsLoaded = true;
    } finally {
      // Hide loading indicator
      if (this.loadingIndicator) {
        setTimeout(() => {
          this.loadingIndicator.style.opacity = '0';
          console.log('SmoothInfiniteScroll: Hiding loading indicator');
        }, 300);
      }
      
      this.isLoading = false;
      console.log('SmoothInfiniteScroll: Preload process complete');
    }
  }
  
  revealPreloadedItems() {
    if (this.preloadedItems.length === 0) return;
    
    console.log('Revealing', this.preloadedItems.length, 'preloaded items with grid position tracking');
    
    // Performance optimization: Use document fragment for batch DOM operations
    const fragment = document.createDocumentFragment();
    
    // Add preloaded items to the fragment with enhanced grid tracking
    this.preloadedItems.forEach((item, index) => {
      // Check if item is a media block
      const isMediaBlock = item.classList.contains('product-item--media-block');
      
      // No spacer logic needed - CSS Grid handles positioning automatically
      // Add animation class and delay based on index
      item.classList.add('infinite-scroll-item');
      
      // Performance optimization: Limit the number of different animation delays
      // Group items into batches of 4 for the same delay
      const delayGroup = Math.floor(index / 4);
      item.style.animationDelay = `${delayGroup * 50}ms`;
      
      // Add to fragment instead of directly to DOM
      fragment.appendChild(item);
    });
    
    // Add all items to the DOM at once (more efficient)
    this.productContainer.appendChild(fragment);
    
    // Ensure media blocks data is available for the CollectionMediaManager
    if (window.collectionMediaManager === undefined && typeof window.restoreMediaBlocksData === 'function') {
      window.restoreMediaBlocksData();
    }
    
    // Initialize product functionality for new items
    this.initializeNewProducts();
    
    // Clear the preloaded items array
    this.preloadedItems = [];
    
    // Update which items are being observed
    this.updateObservedItems();
    
    // Dispatch custom event to notify other scripts
    document.dispatchEvent(this.customEvents.itemsRevealed);
    
    // Ensure media blocks are properly inserted after new products are revealed
    setTimeout(() => {
      // First ensure we have the media blocks data
      if (typeof window.restoreMediaBlocksData === 'function') {
        window.restoreMediaBlocksData();
      }
      
      // Then initialize or refresh the collection media manager
      if (window.collectionMediaManager) {
        // Force a complete re-initialization of media blocks
        window.collectionMediaManager.removeAllMediaBlocks();
        window.collectionMediaManager.insertMediaBlocks();
        console.log('Media blocks re-inserted after infinite scroll');
      } else if (typeof CollectionMediaManager === 'function') {
        window.collectionMediaManager = new CollectionMediaManager();
        console.log('CollectionMediaManager initialized after infinite scroll');
      }
      
      // Force a refresh of all media block styles
      if (typeof this.refreshMediaBlockStyles === 'function') {
        this.refreshMediaBlockStyles();
      } else {
        // Apply styles directly if method doesn't exist
        const mediaBlocks = document.querySelectorAll('.product-item--featured-image');
        if (mediaBlocks.length > 0) {
          console.log(`Applying styles to ${mediaBlocks.length} media blocks`);
          mediaBlocks.forEach(block => {
            // Force a reflow
            void block.offsetWidth;
            
            // Ensure proper display
            block.style.display = 'block';
          });
        }
      }
    }, 800); // Increased timeout for more reliable insertion
    
    // If we've loaded all products, add a class but don't show any message
    if (this.allProductsLoaded) {
      document.body.classList.add('all-products-loaded');
    } else {
      // Preload the next page if we're not at the end
      // Performance optimization: Only preload if we're not already loading
      if (!this.isLoading && this.nextPageUrl) {
        this.preloadNextPage();
      }
    }
  }
  
  /**
   * Force refresh styles on all media blocks to ensure proper display
   */
  refreshMediaBlockStyles() {
    // Select all media blocks
    const mediaBlocks = document.querySelectorAll('.product-item--featured-image');
    
    if (mediaBlocks.length === 0) {
      console.log('No media blocks found to refresh');
      return;
    }
    
    console.log(`Refreshing styles for ${mediaBlocks.length} media blocks`);
    
    // Apply force reflow and ensure CSS properties are applied
    mediaBlocks.forEach(block => {
      // Force a reflow
      void block.offsetWidth;
      
      // Ensure proper display
      block.style.display = 'block';
      
      // Ensure proper positioning based on data attributes
      const width = block.getAttribute('data-media-width') || '1';
      const height = block.getAttribute('data-media-height') || '1';
      
      // Re-apply grid properties
      block.style.gridColumnEnd = `span ${width}`;
      block.style.gridRowEnd = `span ${height}`;
      
      // Ensure images and videos are properly displayed
      const images = block.querySelectorAll('img');
      const videos = block.querySelectorAll('video, iframe');
      
      // Apply proper styles to images
      images.forEach(img => {
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'cover';
        img.style.position = 'absolute';
      });
      
      // Apply proper styles to videos
      videos.forEach(video => {
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.objectFit = 'cover';
        video.style.position = 'absolute';
      });
    });
  }
  
  initializeNewProducts() {
    // Performance optimization: Use requestAnimationFrame for non-critical UI updates
    requestAnimationFrame(() => {
      // Initialize product carousels
      if (typeof window.initProductCarousel === 'function') {
        window.initProductCarousel();
      }
      
      // Initialize product swiper with performance optimizations
      const newSwipers = document.querySelectorAll('.infinite-scroll-item .product-item__carousel');
      if (newSwipers.length > 0 && typeof Swiper !== 'undefined') {
        // Performance optimization: Process swipers in batches
        const initializeSwipers = (startIndex, batchSize) => {
          const endIndex = Math.min(startIndex + batchSize, newSwipers.length);
          
          for (let i = startIndex; i < endIndex; i++) {
            const swiperEl = newSwipers[i];
            if (!swiperEl.swiper && !swiperEl.classList.contains('swiper-initialized')) {
              new Swiper(swiperEl, {
                slidesPerView: 1,
                spaceBetween: 0,
                watchOverflow: true,
                threshold: 2,
                lazy: true, // Enable lazy loading for better performance
                preloadImages: false, // Don't preload all images
                pagination: {
                  el: swiperEl.querySelector('.swiper-pagination'),
                  clickable: true
                }
              });
            }
          }
          
          // Process next batch if needed
          if (endIndex < newSwipers.length) {
            setTimeout(() => {
              initializeSwipers(endIndex, batchSize);
            }, 10);
          }
        };
        
        // Start initializing swipers in batches of 5
        initializeSwipers(0, 5);
      }
      
      // Ensure CollectionMediaManager is initialized and has access to media blocks data
      if (window.collectionMediaManager) {
        // If we already have an instance, just trigger a reinitialization
        setTimeout(() => {
          window.collectionMediaManager.insertMediaBlocks();
        }, 300);
      } else if (typeof window.restoreMediaBlocksData === 'function') {
        // Try to restore media blocks data and initialize manager
        window.restoreMediaBlocksData();
        setTimeout(() => {
          if (!window.collectionMediaManager) {
            window.collectionMediaManager = new CollectionMediaManager();
          } else {
            window.collectionMediaManager.insertMediaBlocks();
          }
        }, 300);
      }
      
      // Initialize hover effects if needed - optimize with fewer DOM operations
      const newHoverItems = document.querySelectorAll('.infinite-scroll-item .product-item');
      if (newHoverItems.length > 0) {
        for (let i = 0; i < newHoverItems.length; i++) {
          const links = newHoverItems[i].querySelectorAll('a');
          for (let j = 0; j < links.length; j++) {
            if (!links[j].hasAttribute('data-no-instant')) {
              links[j].setAttribute('data-no-instant', '');
            }
          }
        }
      }
      
      // Update URL with current page for better history navigation
      // Only update history state if we're not already on that page
      if (this.currentPage > 1 && window.history && window.history.pushState) {
        const url = new URL(window.location.href);
        const currentUrlPage = parseInt(url.searchParams.get('page') || '1');
        
        if (currentUrlPage !== this.currentPage) {
          url.searchParams.set('page', this.currentPage);
          window.history.pushState({ page: this.currentPage }, '', url.toString());
        }
      }
    });
  }

  // Clean up resources when needed
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      console.log('SmoothInfiniteScroll: Observer disconnected on destroy.');
    }
  }
}

// Debug helper functions
window.enableGridDebug = function() {
  document.body.setAttribute('data-debug', 'true');
  console.log('Grid debug mode enabled - spacers will be visible');
};

// Initialize smooth infinite scroll when the DOM is fully loaded
document.addEventListener('DOMContentLoaded', () => {
  console.log('SmoothInfiniteScroll: DOM loaded, checking for collection page...');
  
  // Only initialize on collection pages
  const collectionPage = document.querySelector('.collection-products');
  console.log('SmoothInfiniteScroll: Collection page found:', !!collectionPage);
  
  if (collectionPage) {
    console.log('SmoothInfiniteScroll: Creating instance...');
    window.siedresInfiniteScroll = new SmoothInfiniteScroll();
    
    // Make sure we have the media blocks data available for infinite scroll
    // This is important for pages beyond the first page where the data might not be in the HTML
    if (!window.collectionMediaBlocks && typeof window.restoreMediaBlocksData === 'function') {
      window.restoreMediaBlocksData();
    }
    
    // Handle browser back/forward navigation
    window.addEventListener('popstate', (event) => {
      console.log('SmoothInfiniteScroll: Popstate event triggered:', event.state);
      if (event.state && event.state.page) {
        // Try to restore media blocks data before reload
        if (typeof window.restoreMediaBlocksData === 'function') {
          window.restoreMediaBlocksData();
        }
        window.location.reload();
      }
    });
  } else {
    console.log('SmoothInfiniteScroll: Not a collection page, skipping initialization.');
  }
});
